/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { setUserData } from '../user/actions';

export interface GuidedTourState {
  isActive: boolean;
  isPaused: boolean;
  stepHistory: string[];
  isCompleted: boolean;
  bannerFirstShownAt?: number;
  furthestStepIndex: number;
  furthestStepId?: string;
  userId?: string;
  mobileWarningDismissed?: boolean;
}

const initialState: GuidedTourState = {
  isActive: false,
  isPaused: false,
  stepHistory: [],
  isCompleted: false,
  furthestStepIndex: -1,
  mobileWarningDismissed: false,
};

const guidedTourSlice = createSlice({
  name: 'guidedTour',
  initialState,
  reducers: {
    startTour: (state) => {
      state.isActive = true;
      state.isPaused = false;
      state.stepHistory = ['0-modal'];
      state.isCompleted = false;
      state.bannerFirstShownAt = Date.now();
    },
    restartTour: (state) => {
      state.isActive = true;
      state.isPaused = false;
      state.stepHistory = ['0-modal'];
      state.isCompleted = false;
      state.bannerFirstShownAt = Date.now();
      state.furthestStepIndex = -1;
      state.furthestStepId = undefined;
    },
    pauseTour: (state) => {
      state.isPaused = true;
    },
    resumeTour: (state) => {
      state.isPaused = false;
    },
    goToStep: (state, { payload }: PayloadAction<string>) => {
      state.stepHistory = [...state.stepHistory, payload];
      state.isPaused = false;
    },
    // Rewind in one update to avoid intermediate renders for skipped ON_CLOSE steps.
    goBackTo: (state, { payload }: PayloadAction<string>) => {
      if (payload === '0-modal') return;
      const idx = state.stepHistory.lastIndexOf(payload);
      if (idx < 0 || idx === state.stepHistory.length - 1) return;
      state.stepHistory = state.stepHistory.slice(0, idx + 1);
    },
    completeTour: (state) => {
      state.isCompleted = true;
      state.isActive = false;
    },
    setFurthestStep: (state, { payload }: PayloadAction<{ stepIndex: number; stepId: string }>) => {
      if (payload.stepIndex > state.furthestStepIndex) {
        state.furthestStepIndex = payload.stepIndex;
        state.furthestStepId = payload.stepId;
      }
    },
    dismissMobileWarning: (state) => {
      state.mobileWarningDismissed = true;
    },
  },
  extraReducers: (builder) => {
    // When user data loads, preserve tour state for the same user.
    // If a different user logs in on the same device, reset to a clean state for them.
    builder.addCase(setUserData, (state, action) => {
      const incomingUserId = (action.payload as any).userId as string | undefined;
      if (!state.userId || state.userId === incomingUserId) {
        state.userId = incomingUserId;
      } else {
        Object.assign(state, initialState, { userId: incomingUserId });
      }
    });
  },
});

export default guidedTourSlice;
